import React, {useCallback, useEffect, useMemo, useState} from 'react'
import moment from 'moment'
import {useNavigate} from 'react-router-dom'
import {useQuery} from 'react-query'
import { InterpreterSummaryCard, InterpreterSummaryCards } from './InterpreterSummaryCards'
import { InterpreterEarningsChart, InterpreterEarningsChartData } from './InterpreterEarningsChart'
import { InterpreterMetricSection, InterpreterMetricsSection } from './InterpreterMetricsSection'
import { InterpreterNotification, InterpreterSchedule, InterpreterScheduleItem } from './InterpreterSchedule'
import {
  getInterpreterOverview,
  getInterpreterLanguages,
  type InterpreterDashboardFilters,
  type InterpreterDashboardOverviewDto,
} from './_requests'
import {DashboardFilterBar} from '../components/DashboardFilterBar/DashboardFilterBar'
import {toFilterOptions, lastThirtyDaysRange} from '../components/DashboardFilterBar/utils'
import type {FilterFieldConfig} from '../components/DashboardFilterBar/types'
import {CommonLoading} from '../../../../Utils/commonLoading'
import toaster from '../../../../Utils/toaster'

function formatCurrency(value?: number) {
  return `$${Number(value ?? 0).toLocaleString()}`
}

function ensureNonNegative(value?: number) {
  return Math.max(value ?? 0, 0)
}

function formatMetricNumber(value?: number, fractionDigits = 0) {
  return Number(value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
}

function formatMinutes(value?: number) {
  const totalMinutes = Math.round(value ?? 0)
  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return minutes > 0 ? `${hours}hr ${minutes}m` : `${hours}hr`
  }

  return `${totalMinutes}m`
}

function niceYStep(values: number[], maxIntervals = 5) {
  const max = Math.max(...values, 1)
  for (let magnitude = 1; ; magnitude *= 10) {
    for (const factor of [1, 2, 5]) {
      if (Math.ceil(max / (factor * magnitude)) <= maxIntervals) return factor * magnitude
    }
  }
}

const formatPeriodLabel = (filters: InterpreterDashboardFilters) => {
  const fmt = (d?: string) => (d ? moment(d, 'YYYY-MM-DD').format('MMM DD, YYYY') : '')
  if (filters.startDate && filters.endDate) return `${fmt(filters.startDate)} - ${fmt(filters.endDate)}`
  return 'Selected period'
}

export function InterpreterOverviewV2() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState<InterpreterDashboardOverviewDto | null>(null)

  const defaultRange = useMemo(lastThirtyDaysRange, [])
  const [filters, setFilters] = useState<InterpreterDashboardFilters>(defaultRange)

  const {data: languages, isLoading: isFetchingLanguages} = useQuery(
    'interpreter-dashboard-languages',
    getInterpreterLanguages,
    {
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      onError: () => toaster('error', 'Failed to load your languages'),
    }
  )

  useEffect(() => {
    let cancelled = false

    const loadDashboard = async () => {
      setIsLoading(true)
      try {
        const data = await getInterpreterOverview(filters)
        if (!cancelled) setDashboardData(data)
      } catch (error) {
        if (!cancelled) toaster('error', 'Failed to load interpreter dashboard')
        console.error(error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadDashboard()
    return () => {
      cancelled = true
    }
  }, [filters])

  const periodLabel = useMemo(() => formatPeriodLabel(filters), [filters])
  const hasLanguageFilter = (filters.languages?.length ?? 0) > 0

  const filterFields: FilterFieldConfig[] = useMemo(
    () => [
      {
        id: 'languages',
        iconPath: '/media/icons/ipivis/language.svg',
        placeholder: languages?.length === 0 ? 'No profile languages' : 'All Languages',
        options: toFilterOptions(languages),
        isLoading: isFetchingLanguages,
      },
    ],
    [languages, isFetchingLanguages]
  )

  const summaryCards: InterpreterSummaryCard[] = useMemo(() => {
    const summary = dashboardData?.summary

    return [
      {
        id: 'open-appointments',
        title: 'Open Appointments',
        value: String(summary?.openAppointments ?? 0),
        subtitle: 'All Open Jobs',
        buttonLabel: 'View Assignments',
        buttonVariant: 'outline',
        variant: 'open',
      },
      {
        id: 'confirmed',
        title: 'Confirmed',
        value: String(summary?.confirmedAppointments ?? 0),
        subtitle: 'Next 30 days scheduled',
        buttonLabel: 'View Listings',
        buttonVariant: 'outline',
        variant: 'confirmed',
      },
      {
        id: 'vos-required',
        title: 'VOS Required',
        value: String(summary?.vosRequiredAppointments ?? 0),
        subtitle: 'Requires action to get paid',
        buttonLabel: 'Submit Now',
        buttonVariant: 'solid',
        variant: 'vos',
      },
      {
        id: 'streak',
        title: 'Best streak',
        value: String(summary?.maxStreakDays ?? 0),
        subtitle: `Current: ${summary?.streakDays ?? 0} days in a row`,
        variant: 'streak',
      },
    ]
  }, [dashboardData])

  const earningsChart: InterpreterEarningsChartData = useMemo(() => {
    const earnings = dashboardData?.earningsOverview

    return {
      title: 'Earnings Overview',
      tooltipText:
        'Graph shows your earning trend of past 6 months based on the approved invoices. Not affected by the dashboard filters.',
      subtitle: 'Performance metrics for the last 6 months',
      totalLabel: 'Total Approved',
      totalValue: formatCurrency(ensureNonNegative(earnings?.totalApproved)),
      labels: earnings?.points?.map((point) => point.label) ?? [],
      values: earnings?.points?.map((point) => ensureNonNegative(point.value)) ?? [],
      legendLabel: 'Estimated Earnings',
      yPrefix: '$',
    }
  }, [dashboardData])

  const metricSections: InterpreterMetricSection[] = useMemo(() => {
    const opi = dashboardData?.opiMetrics
    const ipi = dashboardData?.ipiMetrics
    const opiValues = opi?.growthPoints?.map((point) => point.value) ?? []
    const ipiValues = ipi?.growthPoints?.map((point) => point.value) ?? []

    return [
      {
        id: 'opi',
        heading: 'Over the Phone (OPI) Metrics',
        period: periodLabel,
        cards: [
          {
            id: 'opi-total-calls',
            label: 'Total Calls',
            value: formatMetricNumber(opi?.totalCalls),
            iconSrc: '/media/interpreter-dashboard/IMG_5.svg',
          },
          {
            id: 'opi-total-minutes',
            label: 'Total Minutes',
            value: formatMetricNumber(opi?.totalMinutes),
            iconSrc: '/media/interpreter-dashboard/IMG_21.svg',
          },
          {
            id: 'opi-avg-duration',
            label: 'Avg. Duration',
            value: formatMinutes(opi?.avgDurationMinutes),
            iconSrc: '/media/interpreter-dashboard/IMG_22.svg',
          },
          {
            id: 'opi-avg-wait',
            label: 'Avg Time Spend',
            value: formatMinutes(opi?.avgWaitMinutes),
            subtitle: hasLanguageFilter ? 'Not filtered by language' : undefined,
            iconSrc: '/media/interpreter-dashboard/IMG_23.svg',
          },
        ],
        chart: {
          title: 'OPI Volume Growth',
          tooltipText:
            'Calls on each day you had calls in the selected period (one bar per month for long ranges). Days without calls are not shown, and the bars add up to Total Calls.',
          period: periodLabel,
          labels: opi?.growthPoints?.map((point) => point.label) ?? [],
          values: opiValues,
          yStep: niceYStep(opiValues),
          barGap: 50,
          maxBarThickness: 56,
          emptyText: 'No calls in this period',
        },
      },
      {
        id: 'ipi',
        heading: 'In Person(IPI) & Virtual(VIS) Metrics',
        period: periodLabel,
        cards: [
          {
            id: 'ipi-completed',
            label: 'Completed IPI',
            value: formatMetricNumber(ipi?.completedIpi),
            subtitle: 'Physical appointments',
            iconSrc: '/media/interpreter-dashboard/IMG_25.svg',
          },
          {
            id: 'ipi-payable-hours',
            label: 'Payable Hours',
            value: formatMetricNumber(ipi?.payableHours, 1),
            iconSrc: '/media/interpreter-dashboard/IMG_21.svg',
          },
          {
            id: 'ipi-cancel-rate',
            label: 'Cancel Rate',
            value: `${formatMetricNumber(ipi?.cancelRate, 1)}%`,
            iconSrc: '/media/interpreter-dashboard/IMG_8.svg',
          },
          {
            id: 'ipi-acceptance',
            label: 'Acceptance',
            value: `${formatMetricNumber(ipi?.acceptanceRate, 1)}%`,
            iconSrc: '/media/interpreter-dashboard/IMG_23.svg',
          },
        ],
        chart: {
          title: 'In Person & Virtual Interpretation Growth',
          tooltipText:
            'Your assigned in-person and virtual appointments on each day you had one in the selected period (one bar per month for long ranges): confirmed, completed, submitted, or cancelled but payable. Days without appointments are not shown.',
          labels: ipi?.growthPoints?.map((point) => point.label) ?? [],
          values: ipiValues,
          yStep: niceYStep(ipiValues),
          barGap: 50,
          maxBarThickness: 56,
          emptyText: 'No appointments in this period',
        },
      },
    ]
  }, [dashboardData, periodLabel, hasLanguageFilter])

  const handleSummaryCardButtonClick = useCallback(
    (cardId: string) => {
      if (cardId === 'open-appointments') {
        navigate('/appointment/2?status=0,1')
        return
      }
      if (cardId === 'confirmed') {
        navigate('/appointment/2?status=2')
        return
      }
      if (cardId === 'vos-required') {
        navigate('/appointment/2?preset=vos')
      }
    },
    [navigate]
  )

  const scheduleNotification: InterpreterNotification = useMemo(() => {
    const count = dashboardData?.summary?.openAppointments ?? 0

    return {
      id: 'incoming-referrals',
      title: 'Incoming Referrals',
      subtitle: count > 0 ? `${count} invitation${count > 1 ? 's' : ''} waiting for review` : 'No new referrals waiting',
    }
  }, [dashboardData])

  const scheduleItems: InterpreterScheduleItem[] = useMemo(() => {
    return (
      dashboardData?.upcomingSchedule?.map((item) => ({
        id: String(item.appointmentId),
        time: `${new Date(item.startTime).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })} - ${new Date(item.endTime).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })}`,
        client: `${item.customerName} - ${item.language}`,
        location: item.location || '-',
      })) ?? []
    )
  }, [dashboardData])

  return (
    <main
      className='p-4'
      style={{ backgroundColor: '#f8f9fb', fontFamily: "'Manrope', sans-serif" }}
    >
      <div className='mb-4'>
        <DashboardFilterBar
          fields={filterFields}
          defaultStartDate={defaultRange.startDate}
          defaultEndDate={defaultRange.endDate}
          isLoading={isLoading}
          onApply={({startDate, endDate, selections}) =>
            setFilters({startDate, endDate, languages: selections.languages ?? []})
          }
        />
      </div>

      <div className='d-flex flex-column flex-xl-row gap-4'>
        <div className='flex-grow-1' style={{ minWidth: 0 }}>
          <div className='row g-4'>
            <div className='col-12'>
              <InterpreterSummaryCards cards={summaryCards} onCardButtonClick={handleSummaryCardButtonClick} />
            </div>
            <div className='col-12'>
              <InterpreterEarningsChart chart={earningsChart} />
            </div>
            <div className='col-12'>
              <InterpreterMetricsSection sections={metricSections} />
            </div>
          </div>
        </div>
        <div style={{ minWidth: '300px', maxWidth: '320px', width: '100%' }}>
          <InterpreterSchedule
            heading='Upcoming Schedule'
            rangeLabel='Next 30 days'
            notification={scheduleNotification}
            items={scheduleItems}
          />
        </div>
      </div>
      {isLoading && !dashboardData && <CommonLoading />}
    </main>
  )
}
